import Logo from '../../assets/logo-same-color.svg?react';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { Form, Formik, FormikConfig } from 'formik';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { GiftIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components/Button';
import { FormikCheckBox } from '../../components/formik/FormCheckBox';
import { FormikInput } from '../../components/formik/FormInput';
import useQueryParams from '../../hooks/useQueryParams';
import { useValidationTranslations } from '../../hooks/useValidationTranslations';
import { T, useT } from '../../translation/src';
import { useReferralInfoQuery } from '../../types/graphqlTypes';
import { VerificationScreen } from './VerificationCodeForm';

const useValidationSchema = () => {
  const translations = useValidationTranslations();

  const schema = useMemo(
    () =>
      yup.object().shape({
        organisationName: yup.string().trim().required(translations.isRequired),
        firstName: yup.string().trim().required(translations.isRequired),
        lastName: yup.string().trim().required(translations.isRequired),
        email: yup.string().trim().email(translations.validEmail).required(translations.isRequired),
        agreeTerms: yup.bool().oneOf([true], translations.agreeTerms),
        agreeReceiveNews: yup.bool().oneOf([true, false]),
        password: yup.string().trim().required(translations.isRequired),
        confirmPassword: yup
          .string()
          .required(translations.isRequired)
          .oneOf([yup.ref('password')], translations.passwordsMustMatch),
      }),
    [translations],
  );

  return schema;
};

interface FormValues {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  organisationName: string;
  agreeTerms: boolean;
  agreeReceiveNews: boolean;
}

const useTranslations = () => ({
  organisationName: useT('organisation name', { swc: true }),
  firstName: useT('first name', { swc: true }),
  lastName: useT('last name', { swc: true }),
  email: useT('email', { swc: true }),
  password: useT('password', { swc: true }),
  confirmPassword: useT('confirm password', { swc: true }),
  alreadyExists: useT('already exists', { swc: true }),
});

const Register_: React.FC2 = () => {
  const translations = useTranslations();
  const validationTranslations = useValidationTranslations();
  const validationSchema = useValidationSchema();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const turnstileSiteKey = import.meta.env.VITE_NX_TURNSTILE_SITE_KEY;
  // A referral link (/register?ref=<token>): the organisation registered is linked to whoever handed it out.
  // A stale token only loses the banner; the registration itself goes ahead.
  const { ref: referralToken = '' } = useQueryParams(['ref']);
  const { data: referralData } = useReferralInfoQuery({ variables: { token: referralToken }, skip: !referralToken });
  const referrerName = referralData?.referralInfo.organisationName;
  /** Set once the account exists: the form gives way to the code entry. The password stays in memory so a verified code signs the user in directly. */
  const [pendingVerification, setPendingVerification] = useState<{ email: string; password: string } | null>(null);

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values, helpers) => {
      try {
        setIsSubmitting(true);

        // The widget uses execution: 'execute', so no challenge runs until we
        // ask for one here — at submit time. This avoids issuing a fresh
        // challenge every ~5 min for a registration tab left open for days
        // (Turnstile's default auto-refresh). Run the challenge now and wait
        // for the token; reuse an existing one if the user already has it.
        let token = turnstileToken;
        if (turnstileSiteKey && !token) {
          turnstileRef.current?.execute();
          token = (await turnstileRef.current?.getResponsePromise()) ?? null;
          setTurnstileToken(token);
        }
        if (turnstileSiteKey && !token) {
          toast.error(validationTranslations.somethingWentWrong);
          return;
        }

        const response = await fetch(`${import.meta.env.VITE_NX_SERVER}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...values, turnstileToken: token, ...(referralToken && { referralToken }) }),
          credentials: 'include',
        });
        if (response.status === 200) {
          const body = (await response.json().catch(() => ({}))) as { email?: string };
          setPendingVerification({ email: body.email ?? values.email.trim().toLowerCase(), password: values.password });
          return;
        }
        // A used token cannot be re-verified, so reset the widget for a retry.
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        const jsonResponse = await response.json();
        if (jsonResponse.message === 'ALREADY_EXISTS') helpers.setFieldError(jsonResponse.field, translations.alreadyExists);
        else toast.error(validationTranslations.somethingWentWrong);
      } catch (e) {
        console.error('Registration failed:', e);
        turnstileRef.current?.reset();
        setTurnstileToken(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [turnstileToken, turnstileSiteKey, referralToken, translations.alreadyExists, validationTranslations.somethingWentWrong],
  );

  const initialValues: FormikConfig<FormValues>['initialValues'] = useMemo(
    () => ({
      email: '',
      password: '',
      organisationName: '',
      firstName: '',
      lastName: '',
      confirmPassword: '',
      agreeReceiveNews: false,
      agreeTerms: false,
    }),
    [],
  );

  if (pendingVerification) {
    return (
      <VerificationScreen
        email={pendingVerification.email}
        password={pendingVerification.password}
        onEmailChanged={(email) => setPendingVerification({ ...pendingVerification, email })}
        before={<T _str="We sent a 6-digit code to" />}
        after={<T _str="Enter it below to activate your account. The code is valid for 15 minutes." />}
        footer={
          <p className="text-sm text-center">
            <Link className="font-medium text-french-violet-500 hover:text-french-violet-400 dark:text-neon-green-500 dark:hover:text-neon-green-400" to="/login">
              <T _str="go back to login page" />
            </Link>
          </p>
        }
      />
    );
  }

  return (
    <div className="flex items-center justify-center w-full min-h-full px-4 py-12 sm:px-6 lg:px-8 dark:bg-[#313131] dark:text-white overflow-auto h-full">
      <div className="flex flex-col w-full h-full max-w-md space-y-8 overflow-auto">
        <div>
          <Logo className="w-auto mx-auto" />
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-center text-gray-900 dark:text-white">
            <T _str="Create an account" />
          </h2>
          <p className="mt-2 text-sm text-center text-gray-600 dark:text-white">
            <T _str="Or" />{' '}
            <Link className="font-medium text-french-violet-500 hover:text-french-violet-400 dark:text-neon-green-500 dark:hover:text-neon-green-400" to="/login">
              <T _str="sign in to your account" />
            </Link>
          </p>
        </div>
        {referrerName && (
          <div className="flex items-center gap-3 px-4 py-3 mx-2 text-sm border rounded-md border-neon-green-500 bg-neon-green-100 dark:bg-eerie-black">
            <GiftIcon className="w-6 h-6 shrink-0" />
            <span>
              <T _str="You were referred by {name}: your new organisation gets an extra free project." args={{ name: referrerName }} />
            </span>
          </div>
        )}
        <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
          <Form className="px-2 mt-8 overflow-auto">
            <FormikInput autoFocus name="organisationName" label={translations.organisationName} placeholder={translations.organisationName} />
            <div className="mb-2 text-sm italic text-gray-500 dark:text-gray-300">
              <T _str="Do you want to join an existing organisation? Request an invite from the organisation admin to get access." />
            </div>
            <FormikInput name="firstName" label={translations.firstName} placeholder={translations.firstName} />
            <FormikInput name="lastName" label={translations.lastName} placeholder={translations.lastName} />
            <FormikInput name="email" autoComplete="email" label={translations.email} placeholder={translations.email} />
            <FormikInput name="password" type="password" autoComplete="current-password" label={translations.password} />
            <FormikInput name="confirmPassword" type="password" label={translations.confirmPassword} />
            <FormikCheckBox
              name="agreeTerms"
              label={
                <span>
                  <T _str="I agree to Pointorama's Terms and Privacy Policy" />
                </span>
              }
            />
            <FormikCheckBox name="agreeReceiveNews" label={<T _str="I agree to receive Pointorama's news and updates " />} />
            {turnstileSiteKey && (
              // The widget only occupies space when it actually shows something:
              //  - execution 'execute'  → nothing runs until onSubmit calls
              //    execute(), so an idle/open tab issues no challenges.
              //  - appearance 'interaction-only' → on execute it stays invisible
              //    (0 height) when it can pass silently, and only renders the
              //    challenge inline if interaction is genuinely required.
              //  - refreshExpired/Timeout 'never' → no silent background re-solve.
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  options={{ execution: 'execute', appearance: 'interaction-only', refreshExpired: 'never', refreshTimeout: 'never' }}
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                />
              </div>
            )}
            <div className="mt-6">
              <Button variant="primary" type="submit" className="items-center justify-center w-full" loading={isSubmitting}>
                <T _str="get started - free" swc />
              </Button>
            </div>
          </Form>
        </Formik>
      </div>
    </div>
  );
};

export const Register = memo(Register_);
